import React from 'react';
import { PlusCircle, Trash } from 'react-feather';

import PageLayout from '@/components/PageLayout';
import SetHolidays from './SetHolidays';

import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';

import { formatDate, getQueryString } from '@/utils/common.util';
import { useCustomMutation } from '@/utils/useCustomMutation';
import useCurrentAcademicTerm from '@/utils/useCurrentAcademicTerm';
import { deleteMethod } from '@/api/common';
import useSWR from 'swr';
import { ConfirmModal } from '@/components/modal/confirm-modal/ConfirmModal.component';
import { useUrlState } from '@/utils/useUrlState';

const initialFilters = {
  academicYearId: '',
  termId: '',
  dateFrom: '',
  dateTo: '',
};

export default function HolidaysPage() {
  const [filters, setFilters] = useUrlState(initialFilters);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [openSetHolidays, setOpenSetHolidays] = React.useState(false);

  const {
    academicYears,
    terms,
    isLoading: isAcademicLoading,
  } = useCurrentAcademicTerm({
    academicYearId: filters.academicYearId,
    setQuery: setFilters,
  });

  const { data, mutate, isLoading: isHolidaysLoading } = useSWR(
    filters.academicYearId ||
      filters.termId ||
      filters.dateFrom ||
      filters.dateTo
      ? `school-calendar/holidays?${getQueryString(filters)}`
      : 'school-calendar/holidays',
  );

  const { trigger: deleteTrigger, isMutating: isDeleting } = useCustomMutation(
    selectedItem
      ? `school-calendar/holidays/delete/${selectedItem.id}`
      : 'school-calendar/holidays/delete/__none__',
    (url) => deleteMethod(url),
  );

  const holidays = data?.data ?? [];

  const handleAddSuccess = () => {
    mutate();
  };

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Academics', to: '/academic' },
        { label: 'Attendance', to: '/students/attendance' },
        { label: 'Holidays & Breaks', active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">Holidays & Breaks</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setOpenSetHolidays(true)}
          >
            <PlusCircle size="14" strokeWidth="2px" />
            <span>ADD HOLIDAY / BREAK</span>
          </button>
        </div>
      }
      isLoading={isAcademicLoading || isHolidaysLoading}
    >
      <div className="mt-20 card-row-4 gap-20">
        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setFilters({ academicYearId: e.target.value, termId: '' })
          }
          value={filters.academicYearId}
        >
          <SelectItem value="" text="All" />
          {academicYears?.map((year) => (
            <SelectItem
              key={year.id}
              value={String(year.id)}
              text={year.description}
            />
          ))}
        </Select>

        <Select
          labelText="Term"
          disabled={!filters.academicYearId}
          onChange={(e) => setFilters({ termId: e.target.value })}
          value={filters.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem
              key={term.id}
              value={String(term.id)}
              text={term.description}
            />
          ))}
        </Select>

        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          onChange={(value) => {
            if (value.length > 0) {
              setFilters({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setFilters({ dateFrom: '' });
          }}
          onClose={() => {}}
        >
          <DatePickerInput
            id="start-date"
            labelText="Start Date"
            value={filters.dateFrom}
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setFilters({ dateFrom: e.target.value })}
          />
        </DatePicker>

        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          onChange={(value) => {
            if (value.length > 0) {
              setFilters({
                dateTo: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setFilters({ dateTo: '' });
          }}
          onClose={() => {}}
        >
          <DatePickerInput
            id="end-date"
            labelText="End Date"
            value={filters.dateTo}
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setFilters({ dateTo: e.target.value })}
          />
        </DatePicker>
      </div>

      {holidays?.length === 0 && (
        <p className="text-14 mt-30 text-grey">
          No holidays found for the selected filters.
        </p>
      )}

      {holidays?.length > 0 && (
        <div className="mt-30">
          <div style={{ overflow: 'auto' }}>
            <table className="bx--data-table table-white">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {holidays?.map((holiday) => (
                  <tr key={holiday.id}>
                    <td>{holiday.name}</td>
                    <td>{formatDate(holiday.startDate)}</td>
                    <td>
                      {!holiday.isSingleDay && holiday.endDate
                        ? `${formatDate(holiday.endDate)}`
                        : ''}
                    </td>
                    <td>
                      <button
                        className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn"
                        style={{ gap: '4px' }}
                        onClick={() => setSelectedItem(holiday)}
                      >
                        <Trash
                          size="14"
                          strokeWidth="2px"
                          className="text-danger"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SetHolidays
        isOpen={openSetHolidays}
        closeModal={() => setOpenSetHolidays(false)}
        onSuccess={handleAddSuccess}
      />

      <ConfirmModal
        title={`Delete Holiday - ${selectedItem?.name}`}
        isOpen={selectedItem !== null}
        closeModal={() => setSelectedItem(null)}
        description="Are you sure you want to delete this holiday or break? This action cannot be undone."
        actionText="DELETE"
        onClick={() =>
          deleteTrigger(null, {
            onSuccess: () => {
              setSelectedItem(null);
              mutate();
            },
          })
        }
        isLoading={isDeleting}
        actionBtnClasses="danger"
      />
    </PageLayout>
  );
}
